<template>
  <Dialog
    :model-value="modelValue"
    :title="title"
    :subtitle="subtitle"
    :size="size"
    @update:model-value="$emit('update:modelValue', $event)"
    @close="$emit('close')"
  >
    <p v-if="message" class="dlgc-message">{{ message }}</p>

    <div v-if="itemName" class="dlgc-item">
      <span class="dlgc-item-name">{{ itemName }}</span>
      <span v-if="itemDetails" class="dlgc-item-details">{{ itemDetails }}</span>
    </div>

    <DialogNotice v-if="consequences.length" :tone="tone" :title="consequencesTitle">
      {{ irreversible ? "Cette action est définitive et ne peut pas être annulée." : consequencesLead }}
    </DialogNotice>

    <ul v-if="consequences.length" class="dlgc-list">
      <li v-for="(item, i) in normalizedConsequences" :key="i" class="dlgc-line">
        <i :class="item.icon" class="dlgc-line-icon" aria-hidden="true"></i>
        <span>{{ item.text }}</span>
      </li>
    </ul>

    <DialogNotice v-else-if="irreversible" :tone="tone">
      Cette action est définitive et ne peut pas être annulée.
    </DialogNotice>

    <slot></slot>

    <Input
      v-if="requireTyping"
      v-model="typed"
      :label="`Pour confirmer, saisissez « ${requireTyping} »`"
      :placeholder="requireTyping"
      hint="Cette étape n'apparaît que pour les actions qui entraînent des données liées."
      autocomplete="off"
    />

    <template #footer>
      <Button type="button" variant="secondary" :disabled="loading" @click="$emit('close')">
        {{ cancelLabel }}
      </Button>
      <Button
        type="button"
        :variant="confirmVariant"
        :icon="confirmIcon"
        :loading="loading"
        :disabled="blocked"
        @click="$emit('confirm')"
      >
        {{ loading ? loadingLabel : confirmLabel }}
      </Button>
    </template>
  </Dialog>
</template>

<script setup>
/**
 * FENÊTRE DE CONFIRMATION — suppression ou non.
 *
 * Le remplaçant v2 de `ConfirmDeleteModal.vue`, élargi aux vingt-six modales qui
 * confirmaient une action SANS rien supprimer (valider, envoyer, annuler un
 * rendez-vous) et qui n'avaient, elles, aucun composant.
 *
 * Ce qui change dans la façon de poser la question : la v1 centrait le texte
 * sous une icône d'alerte de 48 px et demandait « Êtes-vous sûr ? ». Personne
 * n'est sûr de quoi que ce soit face à une question posée sans information.
 * Ici, la question est remplacée par la RÉPONSE — ce qui est touché
 * (`itemName`), ce que ça entraîne (`consequences`) — et le bouton porte le
 * verbe exact plutôt qu'un « Confirmer » interchangeable.
 *
 * `requireTyping` réserve la saisie du nom aux suppressions qui emportent des
 * données liées : l'exiger partout apprend surtout à la recopier sans lire.
 */
import { computed, ref, watch } from 'vue'
import Dialog from './Dialog.vue'
import DialogNotice from './DialogNotice.vue'
import Button from '../Button.vue'
import Input from '../Input.vue'

const props = defineProps({
  modelValue: { type: Boolean, required: true },
  title: { type: String, default: 'Confirmer l\'action' },
  /** Petite par défaut ; plus grande quand la confirmation montre une carte (élargir une zone). */
  size: { type: String, default: 'small', validator: (v) => ['small', 'large'].includes(v) },
  subtitle: { type: String, default: null },
  message: { type: String, default: null },
  /** Ce sur quoi porte l'action, en toutes lettres. */
  itemName: { type: String, default: null },
  itemDetails: { type: String, default: null },
  /** Ce qui part avec : chaînes, ou objets { text, icon }. */
  consequences: { type: Array, default: () => [] },
  consequencesTitle: { type: String, default: null },
  consequencesLead: { type: String, default: 'Ces éléments sont concernés.' },
  tone: {
    type: String,
    default: 'danger',
    validator: (v) => ['neutral', 'warning', 'danger'].includes(v)
  },
  irreversible: { type: Boolean, default: true },
  /** Texte que l'utilisateur doit recopier pour débloquer l'action. */
  requireTyping: { type: String, default: null },
  /**
   * Bloque l'action tant que le contenu de la fenêtre est incomplet : une
   * confirmation qui demande AUSSI une raison ne peut pas partir sans elle.
   * Un bouton grisé dit pourquoi on n'avance pas ; un bouton cliquable qui ne
   * fait rien laisse croire à une panne.
   */
  confirmDisabled: { type: Boolean, default: false },
  confirmLabel: { type: String, default: 'Supprimer' },
  cancelLabel: { type: String, default: 'Annuler' },
  loadingLabel: { type: String, default: 'Suppression…' },
  loading: { type: Boolean, default: false },
  confirmIcon: { type: String, default: null }
})

defineEmits(['update:modelValue', 'close', 'confirm'])

const typed = ref('')

const confirmVariant = computed(() => (props.tone === 'danger' ? 'danger' : 'primary'))

const normalizedConsequences = computed(() =>
  props.consequences.map((c) =>
    typeof c === 'string'
      ? { text: c, icon: 'fas fa-circle-notch' }
      : { text: c.text, icon: c.icon || 'fas fa-circle-notch' }
  )
)

const blocked = computed(() => {
  if (props.confirmDisabled) return true
  if (!props.requireTyping) return false
  return typed.value.trim().toLowerCase() !== props.requireTyping.trim().toLowerCase()
})

// La saisie de confirmation ne survit pas à la fermeture : la rouvrir doit
// redemander le geste, sinon le garde-fou ne garde plus rien.
watch(() => props.modelValue, (open) => {
  if (!open) typed.value = ''
})
</script>

<style scoped>
.dlgc-message {
  margin: 0;
  font-size: var(--font-size-base);
  line-height: var(--line-height-normal);
  color: var(--color-text-primary);
}

.dlgc-item {
  display: flex;
  flex-direction: column;
  gap: var(--spacing-1);
  padding: var(--spacing-3) var(--spacing-md);
  background: var(--color-bg-secondary);
  border: 1px solid var(--color-border-light);
  border-radius: var(--radius-md);
}

.dlgc-item-name {
  font-size: var(--font-size-base);
  font-weight: var(--font-weight-semibold);
  color: var(--color-text-primary);
}

.dlgc-item-details {
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-tertiary);
}

.dlgc-list {
  margin: 0;
  padding: 0;
  list-style: none;
  display: flex;
  flex-direction: column;
  gap: var(--spacing-2);
}

.dlgc-line {
  display: flex;
  align-items: flex-start;
  gap: var(--spacing-2);
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
}

.dlgc-line-icon {
  flex: 0 0 auto;
  margin-top: 3px;
  font-size: var(--font-size-xs);
  color: var(--color-text-muted);
}
</style>
